import { FeedbackDto, FeedbackFormTypeEnum, QaFormRequirementEnum } from "api/api.typing";
import Badge from "components/Badge";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mapToListType, TASK_LIST_TYPE } from "../task/task.typing";

interface FeedbackListItemProps {
  feedback: FeedbackDto;
  onSkip: (taskId: number) => void;
  onFillFeedback: (taskId: number) => void;
}

export const FeedbackListItem = ({ feedback, onSkip, onFillFeedback }: FeedbackListItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewTask = () => {
    const listType = mapToListType(feedback.taskStatus) ?? TASK_LIST_TYPE.delivered;
    navigate(`/project/task/list/${listType}/${feedback.taskId}`);
  };

  const getFeedbackTypeLabel = () => {
    switch (feedback.feedbackType) {
      case FeedbackFormTypeEnum.MT:
        return t("feedback.mtFeedback");
      case FeedbackFormTypeEnum.QA:
        return t("feedback.qaFeedback");
      case FeedbackFormTypeEnum.STAR:
        return t("feedback.starFeedback");
      case FeedbackFormTypeEnum.DTP:
        return t("feedback.dtpFeedback");
      default:
        return t("feedback.qaFeedback");
    }
  };

  const getFeedbackDescription = () => {
    switch (feedback.feedbackType) {
      case FeedbackFormTypeEnum.MT:
        return t("feedback.mtDescription");
      case FeedbackFormTypeEnum.QA:
        return t("feedback.qaDescription");
      case FeedbackFormTypeEnum.STAR:
        return t("feedback.starDescription");
      case FeedbackFormTypeEnum.DTP:
        return t("feedback.dtpDescription");
      default:
        return t("feedback.qaDescription");
    }
  };

  const getFeedbackBadgeColor = () => {
    switch (feedback.feedbackType) {
      case FeedbackFormTypeEnum.MT:
        return "sky";
      case FeedbackFormTypeEnum.DTP:
        return "goldfish";
      case FeedbackFormTypeEnum.QA:
      case FeedbackFormTypeEnum.STAR:
      default:
        return "green";
    }
  };

  const feedbackTypeLabel = getFeedbackTypeLabel();
  const feedbackDescription = getFeedbackDescription();

  return (
    <div className="flex flex-col justify-between gap-3 p-4 sm:px-6 md:flex-row">
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge color={getFeedbackBadgeColor()}>{feedbackTypeLabel}</Badge>
          {feedback.qaFormRequirement === QaFormRequirementEnum.Mandatory && (
            <Badge color="red" dataTestId={`feedback-${feedback.taskId}-mandatory`}>
              {t("feedback.mandatory")}
            </Badge>
          )}
          <Badge>{feedback.customerName}</Badge>
          <Badge>{feedback.projectName}</Badge>
          <div className="font-semibold" data-test={`feedback-${feedback.taskId}-code`}>
            {feedback.taskCode}
          </div>
        </div>
        <div>
          <span className="font-semibold">{feedback.taskType}</span>
          <span className="text-color-secondary italic">
            {" "}
            - {feedback.sourceLanguage} → {feedback.targetLanguage}
          </span>
        </div>
        <div className="text-color-secondary text-sm">{feedbackDescription}</div>
      </div>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row md:items-start">
        {feedback.qaFormRequirement === QaFormRequirementEnum.Optional && (
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-gray-200 px-4 py-2 text-sm font-medium text-gray-500 shadow-sm hover:bg-gray-200/75 dark:border-transparent dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
            data-test={`feedback-${feedback.taskId}-skip`}
            onClick={() => onSkip(feedback.taskId)}
          >
            {t("feedback.skipFeedback")}
          </button>
        )}
        <button
          type="button"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
          data-test={`feedback-${feedback.taskId}-fill`}
          onClick={() => onFillFeedback(feedback.taskId)}
        >
          {t("feedback.fillFeedback")}
        </button>
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          data-test={`feedback-${feedback.taskId}-view`}
          onClick={handleViewTask}
        >
          {t("feedback.viewTask")}
        </button>
      </div>
    </div>
  );
};
